import type { MeasurementSystem } from "@/v2/utils/measurement";
import { apiClient } from "../apiClient";

const BASE_URL = "/backend/api/v1/admin/app-configurations";

type ApiEnvelope<T> = {
  body: T;
  error: string | null;
};

export interface FeatureFlagSettings {
  enable_wallet?: boolean | null;
  enable_subscriptions?: boolean | null;
  enable_scan_to_ride?: boolean | null;
  enable_clustering?: boolean | null;
  enable_enhanced_payments?: boolean | null;
  enable_wallet_covers_deposit?: boolean | null;
  enable_push_notifications?: boolean | null;
  enable_analytics?: boolean | null;
  enable_crash_reporting?: boolean | null;
}

export interface GpsValidationSettings {
  threshold_meters?: number | null;
  max_accuracy_meters?: number | null;
  require_vehicle_coordinates?: boolean | null;
}

export interface BleValidationSettings {
  timeout?: number | null;
}

export interface RangeValidationSettings {
  enabled?: boolean | null;
  gps?: GpsValidationSettings | null;
  ble?: BleValidationSettings | null;
}

export interface RentalSettings {
  range_validation?: RangeValidationSettings | null;
  end_rental_verify_user_location_within_station?: boolean | null;
  minimum_battery_percentage_for_rental?: number | null;
}

export interface WalletTopupPreset {
  amount_minor: number;
}

export interface WalletTopupCustomAmountSettings {
  enabled?: boolean | null;
  min_amount_minor?: number | null;
  max_amount_minor?: number | null;
}

export interface PaymentSettings {
  available_payment_methods?: Array<"card" | "paypal"> | null;
  wallet_topup_presets?: WalletTopupPreset[] | null;
  wallet_topup_custom_amount?: WalletTopupCustomAmountSettings | null;
}

export interface FraudSettings {
  fraud_prevention?: boolean | null;
  onboarding_deposit_enabled?: boolean | null;
  onboarding_deposit_amount_minor?: number | null;
  // ISO-8601 cutoff: the onboarding deposit applies only to accounts created on/after this
  // instant; earlier accounts are grandfathered. Superadmin-set; blank defaults to enable-time.
  onboarding_deposit_effective_from?: string | null;
  card_fingerprint_block_enabled?: boolean | null;
  phone_country_block_enabled?: boolean | null;
  allowed_phone_countries?: string[] | null;
}

export interface ContactVisibilitySettings {
  show_support_email?: boolean | null;
  show_support_phone?: boolean | null;
}

export interface LegacyCompanySettings {
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface LegacyCitySettings {
  photo_finish?: boolean | null;
  qr_station_verification_enabled?: boolean | null;
  qr_station_verification_distance_threshold_meters?: number | null;
  operational_hour_starts_at?: string | null;
  operational_hour_ends_at?: string | null;
  public_access_enabled?: boolean | null;
  is_mail_verification_required?: boolean | null;
  support_email?: string | null;
  contact_phone?: string | null;
  contact_address?: string | null;
  support_url?: string | null;
  privacy_url?: string | null;
  terms_url?: string | null;
  faq_url?: string | null;
  how_it_works_url?: string | null;
  company?: LegacyCompanySettings | null;
}

export type LegalDocumentKind = "terms" | "privacy";
export type LegalDocumentPlacement = "legal" | "support";
export type RegistrationAcknowledgementKind = "terms";

/**
 * A labelled link shown in the app's Settings screens and pointed at by registration
 * acknowledgements. `kind` means the app already ships its own translated label and icon for
 * this one; `label` stays required regardless, because an app build that does not recognise a
 * newly added kind falls back to it.
 */
export interface LegalDocument {
  key: string;
  kind?: LegalDocumentKind | null;
  label: string;
  url: string;
  placement?: LegalDocumentPlacement | null;
  version?: string | null;
}

/** One checkbox on the registration screen. Array order is display order. */
export interface RegistrationAcknowledgement {
  key: string;
  kind?: RegistrationAcknowledgementKind | null;
  label: string;
  description?: string | null;
  required: boolean;
  documents: string[];
}

export interface RegistrationSettings {
  acknowledgements?: RegistrationAcknowledgement[] | null;
}

export interface LegacySettings {
  defaults?: LegacyCitySettings | null;
  overrides?: LegacyCitySettings | null;
}

export interface AppConfigurationSettings {
  features?: FeatureFlagSettings | null;
  rental?: RentalSettings | null;
  payment?: PaymentSettings | null;
  fraud?: FraudSettings | null;
  contact_visibility?: ContactVisibilitySettings | null;
  user_radius_for_city_data_fetching_in_kilometers?: number | null;
  measurement_system?: MeasurementSystem | null;
  legacy?: LegacySettings | null;
  // The backend requires these two to be set together or not at all: they merge independently
  // and lists replace wholesale, so a scope restating only one could orphan the other scope's
  // document references.
  documents?: LegalDocument[] | null;
  registration?: RegistrationSettings | null;
}

interface OrgAppConfigurationResponse {
  settings: AppConfigurationSettings;
}

interface CityAppConfigurationResponse {
  settings: AppConfigurationSettings | null;
}

export async function getOrgAppConfiguration(
  orgId: string
): Promise<AppConfigurationSettings> {
  const response = await apiClient.get<
    ApiEnvelope<OrgAppConfigurationResponse>
  >(`${BASE_URL}/organizations/${orgId}`);
  return normalizeLoadedSettings(response.data.body.settings);
}

export async function upsertOrgAppConfiguration(
  orgId: string,
  settings: AppConfigurationSettings
): Promise<AppConfigurationSettings> {
  const response = await apiClient.put<
    ApiEnvelope<OrgAppConfigurationResponse>
  >(`${BASE_URL}/organizations/${orgId}`, { settings });
  return response.data.body.settings;
}

/**
 * Sign-up checkboxes are always required: nothing persists a rider's answer, so an optional one
 * would collect a choice and discard it, and the backend now rejects `required: false`. A
 * configuration saved before that rule would otherwise be unsaveable — the editor has no toggle
 * to fix it, so an unrelated edit elsewhere on the page would fail too. Normalising on read
 * fixes it on the next save, and because the dirty check reads the same value it does not
 * register as an unsaved change on its own.
 */
function normalizeLoadedSettings<T extends AppConfigurationSettings | null>(
  settings: T
): T {
  const acknowledgements = settings?.registration?.acknowledgements;
  if (!acknowledgements?.some((acknowledgement) => !acknowledgement.required)) {
    return settings;
  }
  return {
    ...settings,
    registration: {
      ...settings?.registration,
      acknowledgements: acknowledgements.map((acknowledgement) => ({
        ...acknowledgement,
        required: true,
      })),
    },
  } as T;
}

export async function getCityAppConfiguration(
  cityId: number
): Promise<AppConfigurationSettings | null> {
  const response = await apiClient.get<
    ApiEnvelope<CityAppConfigurationResponse>
  >(`${BASE_URL}/cities/${cityId}`);
  return normalizeLoadedSettings(response.data.body.settings);
}

export async function upsertCityAppConfiguration(
  cityId: number,
  settings: AppConfigurationSettings
): Promise<AppConfigurationSettings> {
  const response = await apiClient.put<
    ApiEnvelope<{ settings: AppConfigurationSettings }>
  >(`${BASE_URL}/cities/${cityId}`, { settings });
  return response.data.body.settings;
}
